import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button, Card, Popconfirm, toast, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconTrash } from "@tabler/icons-react";
import { getIconByDatastoreType } from "../DatastoreImage";
import { DatastoreTypeEnum, GET_INTEGRATIONS } from "../../types";
import { IntegrationDetails } from "@/types/integration";
import { deleteDBTIntegration } from "@/helpers/apis";

interface DbtCoreConnectionCardProps {
  integration: IntegrationDetails;
  syncOpAllowed: boolean;
}

export const DbtCoreConnectionCard = ({
  integration,
  syncOpAllowed,
}: DbtCoreConnectionCardProps): JSX.Element => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: deleteIntegration } = useMutation({
    mutationFn: () => deleteDBTIntegration(integration.id),
    onSuccess: () => {
      toast({
        title: "Scheduled for deletion (typically takes up to 24 hours)",
        description: "The dbt Core connection has been scheduled for deletion.",
      });
      queryClient.invalidateQueries({
        queryKey: ["dbt-core-integration"],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_INTEGRATIONS],
      });
      navigate("/settings/datastores");
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.message || "Failed to delete connection",
      });
    },
  });

  const icon = getIconByDatastoreType({
    datastoreType: DatastoreTypeEnum.DBT_CORE,
    className: "al-rounded al-w-[60px] al-h-[60px] al-p-2",
  });

  return (
    <Card className="al-overflow-hidden al-rounded-lg al-border al-border-gray-200 al-h-full">
      <Stack direction="column" className="al-h-full">
        <div className="al-p-4">
          <Stack direction="row" align="center" gap="sm">
            {icon}
            <Stack direction="column" gap="xxs">
              <Typography weight="regular" size="regular">
                {integration.name}
              </Typography>
              <Typography size="sm" className="al-text-brand">
                dbt Core
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="column" gap="xs" className="al-mt-4">
            <Stack direction="row" justify="between">
              <Typography size="sm" className="al-text-muted-foreground">
                Environment
              </Typography>
              <Typography size="sm" weight="medium">
                {integration.environment_name}
              </Typography>
            </Stack>
          </Stack>
        </div>

        <div className="al-border-t al-border-gray-200 al-bg-white al-px-4 al-py-3 al-mt-auto">
          <Stack direction="row" justify="end" align="center">
            {integration.is_deleted ? (
              <Typography size="sm" className="al-text-muted-foreground">
                Scheduled for deletion (typically takes up to 24 hours)
              </Typography>
            ) : (
              syncOpAllowed && (
                <Popconfirm
                  title="Delete Connection"
                  description={`Are you sure you want to delete "${integration.name}"? This will permanently remove all associated data and sync history.`}
                  onConfirm={(e) => {
                    e.preventDefault();
                    deleteIntegration();
                  }}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Delete connection"
                  >
                    <IconTrash size={18} className="al-text-destructive" />
                  </Button>
                </Popconfirm>
              )
            )}
          </Stack>
        </div>
      </Stack>
    </Card>
  );
};
